import classNames from "classnames";
import { ReactNode } from "react";
import { UseListFiltersReturn } from "../../hooks/useListFilters";
import { useTranslations } from "../../hooks/useTranslations";
import { Glyph, RemoteDataStatus } from "../../types";
import SVGNavigateBefore from "../../icons/NavigateBefore.svg?react";
import SVGNavigateNext from "../../icons/NavigateNext.svg?react";
import ErrorText from "./ErrorText";
import Icon from "./Icon";
import LoadingSpinner from "./LoadingSpinner";
import SearchBar from "./SearchBar";
import styles from "./Table.module.css";

interface Props {
  filters?: UseListFiltersReturn;
  searchOptions?: {
    placeholder: string;
    onChange: () => void;
  };
  total?: number;
  head?: ReactNode;
  body: ReactNode[];
  footer?: ReactNode;
  caption?: ReactNode;
  isSelectable?: boolean;
  isTall?: boolean;
  isStriped?: boolean;
  classNames?: {
    tableWrapper?: string;
    table?: string;
    controls?: string;
  };
  remoteDataStatus?: RemoteDataStatus;
  hasNextPage?: boolean;
}

const Table = ({
  filters,
  searchOptions,
  total,
  head,
  body,
  footer,
  caption,
  isSelectable = true,
  isTall = false,
  isStriped = false,
  classNames: stylesOverride,
  remoteDataStatus = "success",
  hasNextPage: hasNextPageProp,
}: Props) => {
  const { i18n } = useTranslations();
  const isSearchable = filters && filters.isSearchable;
  const page = filters?.state.page ?? 1;
  const pageSize = filters?.state.page_size ?? 5;

  const fromItem = (page - 1) * pageSize + 1;
  const toItem = fromItem + body.length - 1;
  const range = fromItem === toItem ? `${toItem}` : `${fromItem} – ${toItem}`;
  const entriesMessage = total
    ? `${range} ${i18n.table.outOf} ${total}`
    : range;

  const hasNextPage =
    hasNextPageProp ?? (total !== undefined ? toItem < total : false);

  return (
    <div className={classNames(styles.wrapper, stylesOverride?.tableWrapper)}>
      <div className={styles.heading}>
        {isSearchable && (
          <SearchBar
            className={styles.search}
            placeholder={
              searchOptions?.placeholder ?? i18n.table.defaultSearchPlaceholder
            }
            value={filters.state.q ?? ""}
            onChange={(term) => {
              searchOptions?.onChange();
              filters.setSearchTerm(term);
            }}
          />
        )}

        <table
          className={classNames(styles.table, stylesOverride?.table, {
            [styles.selectable]: isSelectable,
            [styles.tall]: isTall,
            [styles.striped]: isStriped,
          })}
        >
          {head && <thead>{head}</thead>}
          <tbody>{body}</tbody>
          {footer && <tfoot>{footer}</tfoot>}
          {caption && <caption className={styles.caption}>{caption}</caption>}
        </table>

        {remoteDataStatus === "success" && body.length === 0 && (
          <div className={styles.status}>{i18n.table.noItems}</div>
        )}

        {remoteDataStatus === "validating" && (
          <div className={styles.loadingSpinner}>
            <LoadingSpinner delayed={0} />
          </div>
        )}

        {remoteDataStatus === "failure" && (
          <div className={styles.status}>
            <ErrorText text={i18n.table.loadError} />
          </div>
        )}
      </div>

      {filters && remoteDataStatus === "success" && body.length > 0 && (
        <div className={classNames(styles.controls, stylesOverride?.controls)}>
          <div>{entriesMessage}</div>
          <div className={styles.pagination}>
            <PaginateBtn
              glyph={SVGNavigateBefore}
              title={i18n.table.prevPage(pageSize)}
              disabled={page === 1}
              onClick={filters.prevPage}
            />
            <PaginateBtn
              glyph={SVGNavigateNext}
              disabled={!hasNextPage}
              title={i18n.table.nextPage(pageSize)}
              onClick={filters.nextPage}
            />
          </div>
        </div>
      )}
    </div>
  );
};

interface PaginateBtnProps {
  glyph: Glyph;
  title: string;
  onClick: () => void;
  disabled?: boolean;
}

const PaginateBtn = ({ glyph, title, onClick, disabled }: PaginateBtnProps) => {
  return (
    <button
      className={classNames(styles.paginationButton, {
        [styles.paginationButtonDisabled]: disabled,
      })}
      title={title}
      onClick={onClick}
      disabled={disabled}
    >
      <Icon className={styles.paginationButtonIcon} glyph={glyph} />
    </button>
  );
};

export default Table;
